import { useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { ButtonControl } from "../Buttons/ButtonControl";
import { CheckboxControl } from "../Inputs/CheckboxControl";
import { Modal } from "../Modals/Modal";
import { PatientAdvancedSearchControl } from "../Search/PatientAdvancedSearchControl";

import styles from "./LinkPatientToLabPopup.module.scss";

class LinkPatientPopupOptions {
    public notLinkedWarningMessage: string;
    public ignoreNotLinkedWarningButtonLabel: string;
    public ignoreNotLinkedWarningButtonCallback: () => void;
    public saveButtonLabel: string;

    constructor(options?: Partial<LinkPatientPopupOptions>) {
        (Object as any).assign(this, options);
    }
}

interface ILinkPatientToLabPopupProps {
    defaultText: string;
    isLinkButtonDisable: boolean;
    linkPatientPopupOptions?: LinkPatientPopupOptions;

    onLinkPatient: () => void;
    onCancel: () => void;
    onToggleCreatePatientPopupClick: () => void;
}

function LinkPatientToLabPopup(props: ILinkPatientToLabPopupProps): JSX.Element {
    const [isDoNotShowUntilNextLogOffChecked, setIsDoNotShowUntilNextLogOffChecked] = useState<boolean>(false);
    const { getText } = useTranslation();

    return (
        <Modal
            title={getText("LinkPatientLabel").toTitleCase()}
            draggable
            showX
            maxHeight="80vh"
            onClose={() => props.onCancel()}
            showOverlay
            disableOverlayClose
            overlayZIndex={29}
            top={50}
            width={780}
            centerContent
            footer={(
                <div className={styles.footer}>
                    <div className={styles.checkboxLabel}>
                        {props.linkPatientPopupOptions && props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonCallback && (
                            <CheckboxControl
                                label={getText("DoNotShowUntilNextLogOff")}
                                checked={isDoNotShowUntilNextLogOffChecked}
                                onChange={value => setIsDoNotShowUntilNextLogOffChecked(value)}
                            />
                        )}
                    </div>
                    <div className={styles.footerButtons}>
                        <ButtonControl
                            label={getText("CancelLabel")}
                            className="btn btn-default margin-xs-right"
                            onClick={() => props.onCancel()}
                        />
                        {props.linkPatientPopupOptions && props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonCallback && (
                            <ButtonControl
                                label={props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonLabel}
                                className="btn btn-primary margin-xs-right"
                                onClick={() => {
                                    sessionStore.hidePatientLinkUntilLogoff = isDoNotShowUntilNextLogOffChecked;
                                    props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonCallback();
                                }}
                            />
                        )}
                        <ButtonControl
                            label={props.linkPatientPopupOptions && props.linkPatientPopupOptions.saveButtonLabel || getText("LinkLabel")}
                            variant="brand"
                            onClick={() => {
                                props.onLinkPatient();
                                sessionStore.hidePatientLinkUntilLogoff = isDoNotShowUntilNextLogOffChecked;
                                props.linkPatientPopupOptions && props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonCallback && props.linkPatientPopupOptions.ignoreNotLinkedWarningButtonCallback();
                            }}
                            disabled={props.isLinkButtonDisable}
                        />
                    </div>
                </div>
            )}
        >
            <div>
                <span className={styles.searchPatientLabel}>
                    {props.linkPatientPopupOptions && props.linkPatientPopupOptions.notLinkedWarningMessage || getText("SearchPatientLabel")}
                </span>
                <PatientAdvancedSearchControl
                    defaultTextValue={props.defaultText}
                    isFocusOnLoad={true}
                    componentId="_patientAdvancedSearchLabAssociation"
                    maxResultsToReturn={20}
                    onPatientSelectedOperation={LocalOperations.LabsUI.SelectPatientForLinking}
                    isRenderTextbox={true}
                    isScrollBarActive={true}
                    hvar="-252"
                    isCreatePatientLinkEnable={cacheStore.ClinicSettings.EnableResultsFixes}
                    onAddPatientLinkClick={() => props.onToggleCreatePatientPopupClick()}
                />
            </div>
        </Modal>
    );
}

export { LinkPatientPopupOptions, LinkPatientToLabPopup };
